/**
 * «Screenshot» in the paperclip menu: one frame of the screen the visitor chooses in the browser's own sharing
 * dialog (Chrome offers the current tab first), as a PNG attachment. Never taken silently — the dialog is the
 * browser's consent, and the picture then waits in the composer as an ordinary chip the visitor can remove.
 * Desktop browsers only: phones have no screen capture, so the menu item is not offered there.
 */

type CaptureOptions = DisplayMediaStreamOptions & {
    preferCurrentTab?: boolean;
    selfBrowserSurface?: 'include' | 'exclude';
    surfaceSwitching?: 'include' | 'exclude';
};

export function canCaptureScreen(win: Window, phone: boolean) {
    return !phone && typeof win.navigator.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * `hide` makes the widget invisible for the frame (when the visitor shares this very tab the panel would cover
 * the page) and returns the function that shows it again. Resolves `null` when the visitor cancels the dialog.
 */
export async function captureScreen(win: Window, hide: () => () => void): Promise<File | null> {
    let stream: MediaStream;
    try {
        stream = await win.navigator.mediaDevices.getDisplayMedia({
            video: { displaySurface: 'browser' },
            audio: false,
            preferCurrentTab: true,
            selfBrowserSurface: 'include',
            surfaceSwitching: 'exclude',
        } as CaptureOptions);
    } catch (e) {
        if ((e as DOMException)?.name === 'NotAllowedError' || (e as DOMException)?.name === 'AbortError')
            return null;
        throw e;
    }

    const show = hide();
    try {
        const video = win.document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await video.play();
        // Two frames for the page behind the hidden panel to repaint, and for the stream to deliver it.
        await nextFrame(win);
        await nextFrame(win);
        await new Promise(resolve => win.setTimeout(resolve, 120));

        const canvas = win.document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob)
            throw new Error('screenshot: empty frame');
        return new File([blob], `screenshot-${stamp(new Date())}.png`, { type: 'image/png' });
    } finally {
        stream.getTracks().forEach(track => track.stop());
        show();
    }
}

function nextFrame(win: Window) {
    return new Promise(resolve => win.requestAnimationFrame(() => resolve(null)));
}

function stamp(date: Date) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}
